import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';
import TaskForm from './TaskForm';

export default function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editData, setEditData] = useState({});

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/tasks');
      setTasks(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleTaskCreated = (newTask) => {
    setTasks([...tasks, newTask]);
    setShowForm(false);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await apiCall(`/api/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      setTasks(tasks.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    try {
      await apiCall(`/api/tasks/${taskId}`, { method: 'DELETE' });
      setTasks(tasks.filter((t) => t.id !== taskId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStart = (task) => {
    setEditingTaskId(task.id);
    setEditData({
      title: task.title,
      description: task.description || '',
      assignedTo: task.assigned_to || '',
      dueDate: task.due_date || '',
      status: task.status || 'todo',
    });
  };

  const handleEditSave = async () => {
    try {
      await apiCall(`/api/tasks/${editingTaskId}`, {
        method: 'PUT',
        body: JSON.stringify(editData),
      });
      setTasks(
        tasks.map((t) =>
          t.id === editingTaskId
            ? {
                ...t,
                title: editData.title,
                description: editData.description,
                assigned_to: editData.assignedTo,
                due_date: editData.dueDate,
                status: editData.status,
              }
            : t
        )
      );
      setEditingTaskId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const todoTasks = tasks.filter((t) => t.status === 'todo');
  const inProgressTasks = tasks.filter((t) => t.status === 'in-progress');
  const doneTasks = tasks.filter((t) => t.status === 'done');

  if (loading)
    return <div className="flex items-center justify-center py-12">Loading tasks...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
        <h2 className="text-xl sm:text-2xl font-bold font-headline">Project Tasks</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm sm:text-base">
          {showForm ? 'Cancel' : '+ New task'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && <TaskForm onTaskCreated={handleTaskCreated} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
        <div className="space-y-4">
          <h3 className="font-bold font-headline text-base sm:text-lg text-primary">
            To Do ({todoTasks.length})
          </h3>
          <div className="space-y-2">
            {todoTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isExpanded={expandedTaskId === task.id}
                onExpand={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onEdit={handleEditStart}
              />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold font-headline text-base sm:text-lg text-tertiary">
            In Progress ({inProgressTasks.length})
          </h3>
          <div className="space-y-2">
            {inProgressTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isExpanded={expandedTaskId === task.id}
                onExpand={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onEdit={handleEditStart}
              />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold font-headline text-base sm:text-lg text-secondary">
            Done ({doneTasks.length})
          </h3>
          <div className="space-y-2">
            {doneTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isExpanded={expandedTaskId === task.id}
                onExpand={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onEdit={handleEditStart}
              />
            ))}
          </div>
        </div>
      </div>

      {editingTaskId && (
        <TaskEditModal
          task={editData}
          onSave={handleEditSave}
          onCancel={() => setEditingTaskId(null)}
          onChange={setEditData}
        />
      )}
    </div>
  );
}

function TaskCard({ task, isExpanded, onExpand, onStatusChange, onDelete, onEdit }) {
  return (
    <div className="card space-y-2 p-3">
      <button
        onClick={onExpand}
        className="w-full text-left hover:opacity-75 transition-opacity"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm flex-shrink-0">{isExpanded ? '▼' : '▶'}</span>
              <h4 className="font-bold text-sm sm:text-base break-words">{task.title}</h4>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(task);
              }}
              className="text-primary hover:text-primary-container font-bold text-lg"
              title="Edit task"
            >
              ✏️
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
              className="text-error hover:text-error-container font-bold text-lg"
            >
              ×
            </button>
          </div>
        </div>
      </button>

      {isExpanded ? (
        <div className="space-y-3 mt-3 pt-3 border-t border-outline-variant">
          {task.description && (
            <div>
              <p className="text-xs font-semibold text-on-surface-variant mb-1">Description</p>
              <p className="text-xs sm:text-sm text-on-surface whitespace-pre-wrap break-words">{task.description}</p>
            </div>
          )}
          {task.assigned_to && (
            <div>
              <p className="text-xs font-semibold text-on-surface-variant mb-1">Assigned To</p>
              <p className="text-xs sm:text-sm text-on-surface">{task.assigned_to_name || task.assigned_to}</p>
            </div>
          )}
          {task.due_date && (
            <div>
              <p className="text-xs font-semibold text-on-surface-variant mb-1">Due Date</p>
              <p className="text-xs sm:text-sm text-on-surface">{task.due_date}</p>
            </div>
          )}
          {task.status && (
            <div>
              <p className="text-xs font-semibold text-on-surface-variant mb-1">Status</p>
              <p className="text-xs sm:text-sm text-on-surface capitalize">{task.status.replace('-', ' ')}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-1 sm:gap-2 pt-2">
            {task.status !== 'todo' && (
              <button
                onClick={() => onStatusChange(task.id, 'todo')}
                className="badge badge-primary hover:opacity-80 text-xs"
              >
                To Do
              </button>
            )}
            {task.status !== 'in-progress' && (
              <button
                onClick={() => onStatusChange(task.id, 'in-progress')}
                className="badge badge-tertiary hover:opacity-80 text-xs"
              >
                In Progress
              </button>
            )}
            {task.status !== 'done' && (
              <button
                onClick={() => onStatusChange(task.id, 'done')}
                className="badge badge-secondary hover:opacity-80 text-xs"
              >
                Done
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {task.description && <p className="text-xs sm:text-sm text-on-surface-variant line-clamp-1">{task.description}</p>}
          <div className="flex flex-wrap gap-1 text-xs">
            {task.assigned_to && <span className="text-on-surface-variant">👤 {task.assigned_to_name || task.assigned_to}</span>}
            {task.due_date && <span className="text-on-surface-variant">📅 {task.due_date}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function TaskEditModal({ task, onSave, onCancel, onChange }) {
  const [allowedUsers, setAllowedUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);

  React.useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const data = await apiCall('/api/admin/users');
        setAllowedUsers(data.filter((u) => u.enabled));
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-surface-container-lowest rounded-lg sm:rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-3 sm:p-6 border-b border-outline-variant gap-3 bg-surface-container-lowest">
          <h2 className="font-bold font-headline text-sm sm:text-lg">Edit Task</h2>
          <button
            onClick={onCancel}
            className="text-on-surface-variant hover:text-on-surface font-bold text-2xl flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="p-3 sm:p-6 space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              value={task.title}
              onChange={(e) => onChange({ ...task, title: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Description</label>
            <textarea
              value={task.description}
              onChange={(e) => onChange({ ...task, description: e.target.value })}
              rows="3"
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Assign To</label>
              {loadingUsers ? (
                <div className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface-variant">
                  Loading...
                </div>
              ) : (
                <select
                  value={task.assignedTo || ''}
                  onChange={(e) => onChange({ ...task, assignedTo: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">-- No assignment --</option>
                  {allowedUsers.map((user) => (
                    <option key={user.email} value={user.email}>
                      {user.email}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Due Date</label>
              <input
                type="date"
                value={task.dueDate}
                onChange={(e) => onChange({ ...task, dueDate: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Status</label>
            <select
              value={task.status}
              onChange={(e) => onChange({ ...task, status: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="todo">To Do</option>
              <option value="in-progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <button onClick={onSave} className="btn-primary flex-1 text-sm">
              Save Changes
            </button>
            <button onClick={onCancel} className="btn-secondary flex-1 sm:flex-none text-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
